import { useCallback, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import siteConfig from '../config/site-config.js';
import requestManager from '../utils/requestManager';
import apiFetch from '../utils/apiFetch';
import {
  runDashboardDeviceFetch,
  classifyFetchResponseQuality
} from '../utils/dashboard';
import { planDeviceFetchSuccessEffects } from '../utils/dashboard/deviceFetchScheduleHelpers';
import { dashboardDebug, dashboardWarn } from '../utils/dashboard/dashboardDebug';
import { dedupeMembersForCommanderList } from '../utils/dashboard/deviceIdentity';

const MIN_REQUEST_INTERVAL = 1000;

/**
 * 指挥页设备列表拉取（节流 / 重试 / 成功后自动开轨迹）
 * 注意：成功后不自动飞向队员，仅刷新数据并可选开启实时轨迹绘制。
 *
 * 轨迹参数必须走 ref：自动刷新定时器可能长期持有旧的 fetchDevices 闭包，
 * 若闭包仍是默认 recent/60，会把 display_days 算成 1，从而「概率性」滤掉队员。
 */
export default function useDashboardFetchDevices({
  trackMode,
  trackMinutes,
  trackHours,
  trackDays,
  trackModeApplied,
  startTimeRef,
  endTimeRef,
  membersRef,
  setMembers,
  setLastRefreshTime,
  setRefreshStatus,
  setNetworkQuality,
  onDevicesLoaded,
  /** 每次设备列表抓取成功后回调（用于三态在线：未见新数据 → 暂时离线） */
  onDevicesFetched,
  /** 单兵 App 额外显示天数（管理后台「单兵 App」设置） */
  getAppDisplayDays
}) {
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const isRequestInProgressRef = useRef(false);
  const lastRequestTimeRef = useRef(0);
  const onDevicesLoadedRef = useRef(onDevicesLoaded);
  const onDevicesFetchedRef = useRef(onDevicesFetched);
  const backendDownNotifiedRef = useRef(false);
  const retryTimerRef = useRef(null);
  const trackParamsRef = useRef({
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    trackModeApplied
  });

  onDevicesLoadedRef.current = onDevicesLoaded;
  onDevicesFetchedRef.current = onDevicesFetched;
  trackParamsRef.current = {
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    trackModeApplied
  };

  const fetchDevices = useCallback(async (retryCount = 0) => {
    const {
      trackMode: mode,
      trackMinutes: minutes,
      trackHours: hours,
      trackDays: days,
      trackModeApplied: applied
    } = trackParamsRef.current;

    if (isRequestInProgressRef.current && retryCount === 0) {
      dashboardDebug('设备列表请求正在进行中，跳过重复请求');
      return;
    }
    if (!applied && retryCount === 0) {
      dashboardDebug('轨迹模式设置尚未应用，但允许基本数据加载', {
        trackMode: mode,
        trackMinutes: minutes,
        trackHours: hours,
        trackDays: days
      });
    }
    const now = Date.now();
    if (retryCount === 0 && now - lastRequestTimeRef.current < MIN_REQUEST_INTERVAL) {
      dashboardDebug('请求间隔过短，跳过请求');
      return;
    }

    isRequestInProgressRef.current = true;
    setIsRequestInProgress(true);
    setRefreshStatus('loading');
    lastRequestTimeRef.current = now;
    // 后端重启等导致 fetch 挂死时，避免轮询永久被 isRequestInProgress 挡住
    const safetyUnlockTimer = setTimeout(() => {
      if (isRequestInProgressRef.current) {
        dashboardWarn('设备列表请求超过 45s 未结束，强制解锁轮询');
        isRequestInProgressRef.current = false;
        setIsRequestInProgress(false);
        setRefreshStatus('error');
      }
    }, 45000);
    dashboardDebug(`开始获取设备列表 (第${retryCount + 1}次尝试)`, {
      trackMode: mode,
      trackMinutes: minutes,
      trackHours: hours,
      trackDays: days
    });

    try {
      const result = await runDashboardDeviceFetch({
        retryCount,
        skipGuards: true,
        trackMode: mode,
        trackMinutes: minutes,
        trackHours: hours,
        trackDays: days,
        getStartTime: () => startTimeRef.current || '',
        getEndTime: () => endTimeRef.current || '',
        apiBase: siteConfig.getApiUrl(),
        requestManager,
        apiFetch,
        classifyQuality: classifyFetchResponseQuality,
        authToken: localStorage.getItem('token'),
        appDisplayDays: typeof getAppDisplayDays === 'function' ? getAppDisplayDays() : 0
      });

      if (result.action === 'success') {
        backendDownNotifiedRef.current = false;
        const { devices, responseTime, quality, displayDaysResolved, trackModeSnapshot, memberVisibility } = result;
        const effects = planDeviceFetchSuccessEffects();
        const prevCount = Array.isArray(membersRef?.current) ? membersRef.current.length : 0;
        // 无权限的空名单要落地；有权限却偶发空数组时不要把已有树闪成 0
        const keepExistingRoster = devices.length === 0
          && memberVisibility !== 'none'
          && prevCount > 0;
        setLastRefreshTime(new Date());
        setRefreshStatus('idle');
        if (keepExistingRoster) {
          dashboardWarn('忽略空设备列表，避免刷新把队员树闪空', { memberVisibility, prevCount });
        } else {
        dashboardDebug('当前轨迹模式设置:', trackModeSnapshot);
        dashboardDebug(`使用${displayDaysResolved.source === 'trackMode' ? '轨迹模式' : displayDaysResolved.source === 'mapSettings' ? '地图显示' : '默认'}设置: ${displayDaysResolved.reason}`);
        if (membersRef) membersRef.current = dedupeMembersForCommanderList(devices);
        setMembers(dedupeMembersForCommanderList(devices));
        onDevicesFetchedRef.current?.(devices);
        dashboardDebug(`设备列表获取成功，共${devices.length}个设备，响应时间: ${responseTime}ms`);
        setNetworkQuality(quality);
        if (effects.shouldAutoStartTrackDrawing && !effects.shouldAutoFlyToMember) {
          setTimeout(() => {
            onDevicesLoadedRef.current?.(devices);
          }, effects.autoStartTrackDelayMs);
        }
        dashboardDebug('设备数据已加载，保持当前地图视角（不自动飞行）');
        }
        return;
      }

      if (result.action === 'retry') {
        setRefreshStatus('error');
        dashboardWarn(`获取设备列表失败 (第${retryCount + 1}次):`, result.error?.message || result.error);
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => fetchDevices(retryCount + 1), result.retryDelay);
        return;
      }

      if (result.action === 'fallback-success') {
        backendDownNotifiedRef.current = false;
        if (membersRef) membersRef.current = dedupeMembersForCommanderList(result.devices);
        setMembers(dedupeMembersForCommanderList(result.devices));
        onDevicesFetchedRef.current?.(result.devices);
        setLastRefreshTime(new Date());
        setRefreshStatus('idle');
        dashboardDebug('使用默认设置获取设备列表成功');
        return;
      }

      setRefreshStatus('error');
      if (result.unavailable) {
        if (!backendDownNotifiedRef.current) {
          backendDownNotifiedRef.current = true;
          message.warning('后端服务暂不可用（代理连不上 5000 端口），请确认已启动 backend');
        }
        dashboardWarn('设备列表拉取终止：后端/代理不可用');
      } else {
        console.error(`获取设备列表失败 (第${retryCount + 1}次):`, result.error);
        if (result.fallbackError) console.error('获取设备列表失败（备用方案）', result.fallbackError);
      }
    } finally {
      clearTimeout(safetyUnlockTimer);
      isRequestInProgressRef.current = false;
      setIsRequestInProgress(false);
    }
  }, [
    startTimeRef,
    endTimeRef,
    membersRef,
    setMembers,
    setLastRefreshTime,
    setRefreshStatus,
    setNetworkQuality
  ]);

  return { fetchDevices, isRequestInProgress };
}
